import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const VendorOperational = () => {
  const { user, apiCall } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [operationalData, setOperationalData] = useState({
    vacation_mode: false,
    vacation_start: '',
    vacation_end: '',
    default_tax_rate: '18',
    default_listing_duration: '30',
    listing_duration_unit: 'days',
    default_currency: 'INR',
  });

  useEffect(() => {
    fetchOperationalData();
  }, []);

  const fetchOperationalData = async () => {
    try {
      const profileData = await apiCall(`/api/users/${user?.id}`);
      if (profileData.vendor_profile?.operational_settings) {
        setOperationalData(prev => ({ ...prev, ...profileData.vendor_profile.operational_settings }));
      }
    } catch (error) {
      console.error('Failed to fetch operational data:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall(`/api/users/me`, {
        method: 'PUT',
        body: JSON.stringify({
          vendor_profile: {
            operational_settings: operationalData
          }
        })
      });
      toast({ title: 'Success', description: 'Operational preferences updated successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update preferences',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold">Operational Settings</h1>
          <p className="text-gray-500 mt-1">Configure your business operational preferences</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Vacation Mode</CardTitle>
              <CardDescription>Temporarily pause your store</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Vacation Mode</Label>
                  <p className="text-sm text-gray-500">Pause new orders while you're away</p>
                </div>
                <Switch
                  checked={operationalData.vacation_mode}
                  onCheckedChange={(checked) => setOperationalData({ ...operationalData, vacation_mode: checked })}
                />
              </div>
              {operationalData.vacation_mode && (
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={operationalData.vacation_start}
                      onChange={(e) => setOperationalData({ ...operationalData, vacation_start: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={operationalData.vacation_end}
                      onChange={(e) => setOperationalData({ ...operationalData, vacation_end: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Default Settings</CardTitle>
              <CardDescription>Set default values for your listings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Tax Rate (%)</Label>
                  <Input
                    type="number"
                    value={operationalData.default_tax_rate}
                    onChange={(e) => setOperationalData({ ...operationalData, default_tax_rate: e.target.value })}
                    placeholder="18"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Currency</Label>
                  <Select
                    value={operationalData.default_currency}
                    onValueChange={(value) => setOperationalData({ ...operationalData, default_currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Listing Duration</Label>
                  <Input
                    type="number"
                    value={operationalData.default_listing_duration}
                    onChange={(e) => setOperationalData({ ...operationalData, default_listing_duration: e.target.value })}
                    placeholder="30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration Unit</Label>
                  <Select
                    value={operationalData.listing_duration_unit}
                    onValueChange={(value) => setOperationalData({ ...operationalData, listing_duration_unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                      <SelectItem value="months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default VendorOperational;
